import { useState, useEffect } from "react";
import AdminSidebar from "@/pages/admin/components/AdminSidebar";
import { getSupabase } from "@/lib/supabase";

interface CryptoTransaction {
  id: number;
  user_id: string | null;
  user_email: string | null;
  user_display_name: string | null;
  wallet_id: number | null;
  coin_type: string;
  network: string | null;
  amount: string | null;
  transaction_hash: string | null;
  status: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface CryptoWallet {
  id: number;
  coin_type: string;
  network: string | null;
  wallet_address: string;
}

const COIN_INFO: Record<string, { color: string; bg: string }> = {
  BTC: { color: "text-amber-400", bg: "bg-amber-900/20" },
  ETH: { color: "text-indigo-600", bg: "bg-indigo-50" },
  USDT: { color: "text-teal-600", bg: "bg-teal-50" },
  LTC: { color: "text-slate-600", bg: "bg-slate-50" },
  XMR: { color: "text-orange-600", bg: "bg-orange-50" },
};

const STATUS_MAP: Record<string, { label: string; classes: string }> = {
  pending: { label: "Pending", classes: "bg-yellow-500/20 text-yellow-200" },
  paid: { label: "Paid", classes: "bg-green-500/20 text-green-200" },
  not_received: { label: "Not Received", classes: "bg-gray-500/20 text-gray-200" },
  payment_failed: { label: "Payment Failed", classes: "bg-red-500/20 text-red-200" },
};

export default function AdminTransactionsPage() {
  const [transactions, setTransactions] = useState<CryptoTransaction[]>([]);
  const [wallets, setWallets] = useState<CryptoWallet[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [coinFilter, setCoinFilter] = useState<string>("all");
  const [selectedTransaction, setSelectedTransaction] = useState<CryptoTransaction | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [csvExporting, setCsvExporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const supabase = getSupabase();
      const [txRes, walletsRes] = await Promise.all([
        supabase.from("crypto_transactions").select("*").order("created_at", { ascending: false }),
        supabase.from("crypto_wallets").select("*"),
      ]);
      setTransactions(txRes.data || []);
      setWallets(walletsRes.data || []);
    } catch {
      // fallback
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (tx: CryptoTransaction, newStatus: string) => {
    setUpdatingId(tx.id);
    try {
      const supabase = getSupabase();
      const { error } = await supabase
        .from("crypto_transactions")
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq("id", tx.id);
      if (error) throw error;
      setTransactions((prev) =>
        prev.map((t) => (t.id === tx.id ? { ...t, status: newStatus } : t))
      );

      // Send email notification to user for paid/failed statuses
      if ((newStatus === "paid" || newStatus === "payment_failed") && tx.user_email) {
        const statusLabel = newStatus === "paid" ? "Paid" : "Payment Failed";
        setToast(`✉ Email sent to ${tx.user_email} — ${statusLabel} confirmation`);
        setTimeout(() => setToast(null), 4000);
        supabase.functions.invoke("payment-status-notify", {
          body: {
            id: tx.id,
            user_email: tx.user_email,
            user_display_name: tx.user_display_name,
            coin_type: tx.coin_type,
            network: tx.network,
            amount: tx.amount,
            transaction_hash: tx.transaction_hash,
            status: newStatus,
          },
        }).catch(() => {
          // email send failure is non-blocking
        });
      }
    } catch {
      // fallback
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const supabase = getSupabase();
      await supabase.from("crypto_transactions").delete().eq("id", id);
      setTransactions((prev) => prev.filter((t) => t.id !== id));
      setConfirmDeleteId(null);
    } catch {
      // fallback
    }
  };

  const getWalletAddress = (walletId: number | null) => {
    if (!walletId) return "Unknown";
    const w = wallets.find((x) => x.id === walletId);
    return w ? w.wallet_address : "Unknown";
  };

  const getCoinInfo = (coin: string) => COIN_INFO[coin] || { color: "text-gray-400", bg: "bg-[#161b22]" };

  const coinTypes = [...new Set(transactions.map((t) => t.coin_type))];

  const filtered = transactions.filter((t) => {
    const s = search.toLowerCase();
    const matchSearch =
      !s ||
      (t.user_email || "").toLowerCase().includes(s) ||
      (t.user_display_name || "").toLowerCase().includes(s) ||
      (t.transaction_hash || "").toLowerCase().includes(s) ||
      (t.coin_type || "").toLowerCase().includes(s) ||
      (t.amount || "").toLowerCase().includes(s);
    const matchStatus = statusFilter === "all" || t.status === statusFilter;
    const matchCoin = coinFilter === "all" || t.coin_type === coinFilter;
    return matchSearch && matchStatus && matchCoin;
  });

  const handleExportCSV = () => {
    setCsvExporting(true);
    try {
      const dataToExport = filtered.length > 0 ? filtered : transactions;

      const headers = [
        "ID", "User Name", "User Email", "Coin", "Network",
        "Amount", "Tx Hash", "Wallet Address", "Status", "Notes", "Date"
      ];

      const escapeCSV = (val: string | null | undefined): string => {
        if (val === null || val === undefined) return "";
        const str = String(val);
        if (str.includes(",") || str.includes('"') || str.includes("\n")) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const rows = dataToExport.map((tx) => [
        tx.id,
        escapeCSV(tx.user_display_name),
        escapeCSV(tx.user_email),
        tx.coin_type,
        escapeCSV(tx.network),
        escapeCSV(tx.amount),
        escapeCSV(tx.transaction_hash),
        escapeCSV(getWalletAddress(tx.wallet_id)),
        tx.status,
        escapeCSV(tx.notes),
        new Date(tx.created_at).toISOString(),
      ]);

      const csvContent = [
        headers.join(","),
        ...rows.map((row) => row.join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const timestamp = new Date().toISOString().split("T")[0];
      link.download = `transactions-export-${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      // fallback
    } finally {
      setCsvExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#161b22]">
      <AdminSidebar />
      <div className="ml-60">
        <header className="bg-[#0d1117] border-b border-gray-700 px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between sticky top-0 z-30">
          <div>
            <h1 className="text-lg font-bold text-gray-200">Transaction Log</h1>
  <p className="text-xs text-gray-500">{transactions.length} total transactions &mdash; all start as pending until admin confirms</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by email, hash, amount..."
                className="pl-9 pr-4 py-2 border border-gray-700 rounded-lg text-xs w-64 focus:outline-none focus:border-[#5cb85c]"
              />
            </div>
            <button
              onClick={fetchData}
              className="w-9 h-9 rounded-lg hover:bg-[#1c2128] flex items-center justify-center text-gray-400 hover:text-[#5cb85c] transition-all"
              title="Refresh"
            >
              <i className="fas fa-sync-alt text-xs" />
            </button>
            <button
              onClick={handleExportCSV}
              disabled={csvExporting || transactions.length === 0}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#5cb85c] hover:bg-[#4cae4c] disabled:bg-gray-300 text-white text-xs font-semibold rounded-lg transition-all whitespace-nowrap"
              title="Export to CSV"
            >
              {csvExporting ? (
                <>
                  <i className="fas fa-spinner fa-spin text-xs" />
                  Exporting...
                </>
              ) : (
                <>
                  <i className="fas fa-download text-xs" />
                  Export CSV
                </>
              )}
            </button>
          </div>
        </header>

        {/* Filters */}
        <div className="px-6 pt-4 flex flex-wrap items-center gap-3">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Filter:</span>
          <div className="flex items-center gap-1.5">
            {["all", "pending", "paid", "not_received", "payment_failed"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-all whitespace-nowrap ${
                  statusFilter === s
                    ? "bg-[#5cb85c] text-white"
                    : "bg-[#1c2128] text-gray-500 hover:bg-[#21262d]"
                }`}
              >
                {s === "all" ? "All Status" : (STATUS_MAP[s]?.label || s)}
              </button>
            ))}
          </div>
          <div className="w-px h-5 bg-gray-200 hidden sm:block" />
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setCoinFilter("all")}
              className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-all whitespace-nowrap ${
                coinFilter === "all" ? "bg-gray-800 text-white" : "bg-[#1c2128] text-gray-500 hover:bg-[#21262d]"
              }`}
            >
              All Coins
            </button>
            {coinTypes.map((coin) => (
              <button
                key={coin}
                onClick={() => setCoinFilter(coin)}
                className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-all whitespace-nowrap ${
                  coinFilter === coin
                    ? "bg-gray-800 text-white"
                    : "bg-[#1c2128] text-gray-500 hover:bg-[#21262d]"
                }`}
              >
                {coin}
              </button>
            ))}
          </div>
        </div>

        <main className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <i className="fas fa-spinner fa-spin text-[#5cb85c] text-2xl" />
            </div>
          ) : (
            <>
              {/* Admin Review Notice */}
              <div className="mb-4 p-4 bg-yellow-900/10 border border-yellow-700/30 rounded-xl flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-yellow-900/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="fas fa-info-circle text-yellow-400 text-sm" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-yellow-400 mb-0.5">Admin Review Required</p>
                  <p className="text-xs text-yellow-500/80 leading-relaxed">
                    All transactions start as <strong>Pending</strong>. You must manually verify each payment on-chain and set the status to <strong>Paid</strong>, <strong>Not Received</strong>, or <strong>Payment Failed</strong>.
                  </p>
                </div>
              </div>

            <div className="bg-[#0d1117] rounded-xl border border-gray-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#161b22] border-b border-gray-800">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">User</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Coin</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Amount</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Tx Hash</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Wallet</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase hidden xl:table-cell">Date</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase w-28">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filtered.map((tx) => {
                      const coin = getCoinInfo(tx.coin_type);
                      const status = STATUS_MAP[tx.status] || STATUS_MAP.pending;
                      return (
                        <tr key={tx.id} className="hover:bg-[#161b22]/50">
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full ${coin.bg} flex items-center justify-center ${coin.color} text-xs font-bold`}>
                                {tx.user_display_name ? tx.user_display_name.charAt(0).toUpperCase() : tx.user_email ? tx.user_email.charAt(0).toUpperCase() : "?"}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-200">{tx.user_display_name || "Anonymous"}</p>
                                <p className="text-[11px] text-gray-400">{tx.user_email || "No email"}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-1.5">
                              <span className={`text-xs font-bold ${coin.color}`}>{tx.coin_type}</span>
                              {tx.network && (
                                <span className="text-[10px] text-gray-400 bg-[#1c2128] px-1.5 py-0.5 rounded">{tx.network}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-3.5">
                            <span className="text-sm font-semibold text-gray-200 font-mono">{tx.amount || "—"}</span>
                          </td>
                          <td className="px-5 py-3.5 hidden md:table-cell">
                            {tx.transaction_hash ? (
                              <span className="text-xs text-gray-500 font-mono truncate max-w-[140px] block" title={tx.transaction_hash}>
                                {tx.transaction_hash.slice(0, 10)}...
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-5 py-3.5 hidden lg:table-cell">
                            <span className="text-xs text-gray-500 font-mono truncate max-w-[120px] block" title={getWalletAddress(tx.wallet_id)}>
                              {getWalletAddress(tx.wallet_id).slice(0, 8)}...
                            </span>
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            <select
                              value={tx.status}
                              onChange={(e) => handleStatusChange(tx, e.target.value)}
                              disabled={updatingId === tx.id}
                              className={`text-[11px] font-semibold px-2 py-1 rounded-full border border-gray-300 cursor-pointer bg-white text-black ${updatingId === tx.id ? "opacity-50" : ""}`}
                            >
                              <option value="pending">Pending</option>
                              <option value="paid">Paid</option>
                              <option value="not_received">Not Received</option>
                              <option value="payment_failed">Payment Failed</option>
                            </select>
                          </td>
                          <td className="px-5 py-3.5 hidden xl:table-cell">
                            <span className="text-xs text-gray-500">
                              {new Date(tx.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => setSelectedTransaction(tx)}
                                className="w-7 h-7 rounded-lg hover:bg-[#1c2128] flex items-center justify-center text-gray-400 hover:text-[#5cb85c] transition-all"
                                title="View details"
                              >
                                <i className="fas fa-eye text-xs" />
                              </button>
                              <button
                                onClick={() => setConfirmDeleteId(tx.id)}
                                className="w-7 h-7 rounded-lg hover:bg-red-900/20 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all"
                                title="Delete"
                              >
                                <i className="fas fa-trash-alt text-xs" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-5 py-16 text-center">
                          <div className="w-12 h-12 bg-[#1c2128] rounded-full flex items-center justify-center mx-auto mb-3">
                            <i className="fas fa-receipt text-gray-400" />
                          </div>
                          <p className="text-sm text-gray-500">No transactions found</p>
                          <p className="text-xs text-gray-400 mt-1">Try adjusting your filters or search</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            </>
          )}
        </main>
      </div>

      {/* Detail Modal */}
      {selectedTransaction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-8" onClick={() => setSelectedTransaction(null)}>
          <div className="bg-[#0d1117] rounded-xl w-full max-w-md mx-4 p-6 my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold text-gray-200">Transaction Details</h3>
              <button onClick={() => setSelectedTransaction(null)} className="w-7 h-7 rounded-lg hover:bg-[#1c2128] flex items-center justify-center text-gray-400">
                <i className="fas fa-times text-sm" />
              </button>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Transaction ID</span>
                <span className="text-xs font-mono text-gray-200">#{selectedTransaction.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">User</span>
                <span className="text-xs font-semibold text-gray-200">{selectedTransaction.user_display_name || "Anonymous"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Email</span>
                <span className="text-xs text-gray-200">{selectedTransaction.user_email || "N/A"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Coin</span>
                <span className="text-xs font-bold text-gray-200">{selectedTransaction.coin_type}{selectedTransaction.network ? ` (${selectedTransaction.network})` : ""}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Amount</span>
                <span className="text-xs font-mono font-semibold text-gray-200">{selectedTransaction.amount || "N/A"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Status</span>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${(STATUS_MAP[selectedTransaction.status] || STATUS_MAP.pending).classes}`}>
                  {(STATUS_MAP[selectedTransaction.status] || STATUS_MAP.pending).label}
                </span>
              </div>
              {selectedTransaction.transaction_hash && (
                <div>
                  <span className="text-xs text-gray-500 block mb-1">Transaction Hash</span>
                  <p className="text-xs font-mono text-gray-300 bg-[#161b22] p-2 rounded break-all">{selectedTransaction.transaction_hash}</p>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Wallet Address</span>
                <span className="text-xs font-mono text-gray-300 max-w-[220px] truncate">{getWalletAddress(selectedTransaction.wallet_id)}</span>
              </div>
              {selectedTransaction.notes && (
                <div>
                  <span className="text-xs text-gray-500 block mb-1">Notes</span>
                  <p className="text-xs text-gray-300 bg-[#161b22] p-2 rounded">{selectedTransaction.notes}</p>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Date</span>
                <span className="text-xs text-gray-200">{new Date(selectedTransaction.created_at).toLocaleString()}</span>
              </div>
            </div>
            <button
              onClick={() => setSelectedTransaction(null)}
              className="w-full mt-5 px-4 py-2.5 bg-[#1c2128] hover:bg-[#21262d] text-gray-300 text-sm font-semibold rounded-lg transition-all whitespace-nowrap"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-[100] animate-[slideUp_0.3s_ease-out]">
          <div className="bg-[#0d1117] border border-green-700/50 rounded-xl px-5 py-3.5 flex items-center gap-3 shadow-lg shadow-black/20 max-w-md">
            <div className="w-8 h-8 rounded-full bg-green-900/30 flex items-center justify-center flex-shrink-0">
              <i className="fas fa-envelope text-green-400 text-xs" />
            </div>
            <p className="text-sm text-green-400 font-medium">{toast}</p>
            <button
              onClick={() => setToast(null)}
              className="w-6 h-6 rounded-lg hover:bg-[#1c2128] flex items-center justify-center flex-shrink-0 text-gray-500 hover:text-gray-300 transition-all"
            >
              <i className="fas fa-times text-xs" />
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setConfirmDeleteId(null)}>
          <div className="bg-[#0d1117] rounded-xl w-full max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-exclamation-triangle text-red-500" />
            </div>
            <h3 className="text-base font-bold text-gray-200 text-center mb-2">Delete Transaction?</h3>
            <p className="text-xs text-gray-500 text-center mb-5">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDeleteId(null)} className="flex-1 px-4 py-2.5 border border-gray-700 rounded-lg text-sm font-semibold text-gray-400 hover:bg-[#161b22] transition-all whitespace-nowrap">Cancel</button>
              <button onClick={() => handleDelete(confirmDeleteId)} className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-lg transition-all whitespace-nowrap">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}